import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { CheckCircle } from 'lucide-react';

interface DeliverabilityPoint {
  time_bucket: string;
  sent: number;
  delivered: number;
}

interface DeliverabilityData {
  trend: DeliverabilityPoint[];
  totals: {
    sent: number;
    delivered: number;
    deliveryRate: number;
  };
  granularity: string;
}

interface OverallDeliverabilityProps {
  data: DeliverabilityData | null;
  loading?: boolean;
  title?: string;
}

// Format date based on granularity
const formatDate = (dateStr: string, granularity: string) => {
  const date = new Date(dateStr);

  if (granularity === 'hourly') {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      timeZone: 'UTC',
    });
  }
  if (granularity === 'daily') {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }
  if (granularity === 'weekly') {
    return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
  }
  // monthly
  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  });
};

export function OverallDeliverability({
  data,
  loading,
  title = 'Overall Deliverability',
}: OverallDeliverabilityProps) {
  // Format chart data
  const chartData = useMemo(() => {
    if (!data?.trend) return [];

    return data.trend.map((point) => ({
      date: formatDate(point.time_bucket, data.granularity),
      rawDate: point.time_bucket,
      sent: point.sent,
      delivered: point.delivered,
      rate: point.sent > 0 ? Math.round((point.delivered / point.sent) * 10000) / 100 : 0,
    }));
  }, [data]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-pulse">
            <div className="w-full h-48 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.totals.sent === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          <p>No message data available for this period</p>
        </div>
      </div>
    );
  }

  // Determine color based on delivery rate
  const getRateColor = (rate: number) => {
    if (rate >= 95) return 'text-green-600';
    if (rate >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>

        {/* Summary stats */}
        <div className="flex items-center gap-6 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {data.totals.sent.toLocaleString()}
            </div>
            <div className="text-gray-500">Sent</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {data.totals.delivered.toLocaleString()}
            </div>
            <div className="text-gray-500">Delivered</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${getRateColor(data.totals.deliveryRate)}`}>
              {data.totals.deliveryRate.toFixed(1)}%
            </div>
            <div className="text-gray-500">Rate</div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
              formatter={(value, name) => {
                if (value === undefined) return ['N/A', name ?? 'Unknown'];
                const numValue = typeof value === 'number' ? value : Number(value);
                const nameLower = String(name).toLowerCase();
                if (nameLower === 'rate') return [`${numValue.toFixed(1)}%`, 'Delivery Rate'];
                if (nameLower === 'sent') return [numValue.toLocaleString(), 'Sent'];
                return [numValue.toLocaleString(), 'Delivered'];
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="sent"
              name="Sent"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6', strokeWidth: 2, r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="delivered"
              name="Delivered"
              stroke="#22c55e"
              strokeWidth={2}
              dot={{ fill: '#22c55e', strokeWidth: 2, r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
